import axios from 'axios';
import apiClient from '../plugins/axios';

/** Detect upload payloads for multipart (File, Blob, Vue/observers; cross-realm safe). */
function isUploadFileValue(v) {
  if (v == null || typeof v !== 'object') return false;
  if (typeof File !== 'undefined' && v instanceof File) return true;
  if (Object.prototype.toString.call(v) === '[object File]') return true;
  if (typeof Blob !== 'undefined' && v instanceof Blob) return true;
  return false;
}

function collectMultipartParts(rawParams) {
  const entries = Object.entries(rawParams || {});
  const files = entries.filter(([, v]) => isUploadFileValue(v));
  const scalars = Object.fromEntries(
    entries.filter(([, v]) => !isUploadFileValue(v)),
  );
  return { files, scalars };
}

function multipartTransformRequest() {
  const base = axios.defaults.transformRequest;
  let chain = [];
  if (Array.isArray(base)) {
    chain = [...base];
  } else if (base) {
    chain = [base];
  }
  return [
    ...chain,
    (data, headers) => {
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
      return data;
    },
  ];
}

// Long texts can take well over the default 10s client timeout to synthesize.
const SYNTHESIZE_TIMEOUT_MS = 120000;

/**
 * TTS Service – GET /tts/voices and POST /tts/synthesize, authenticated via the
 * signed-in user's Bearer token.
 */
class TtsService {
  /**
   * List TTS voices (speakers). GET /tts/voices.
   */
  async getVoices() {
    const response = await apiClient.get('/tts/voices');
    return response.data;
  }

  /**
   * Synthesize speech. POST /tts/synthesize → audio/wav.
   * JSON body when params contain no File values; multipart/form-data when any param is a File
   * (scalar params as JSON in field `params`, each file appended under its param name).
   * @param {{ model_id: string, text: string, params?: Record<string, unknown> }} body
   * @returns {Promise<Blob>}
   */
  async synthesize(body) {
    const { model_id: modelId, text, params: rawParams = {} } = body;
    const { files, scalars } = collectMultipartParts(rawParams);

    const parseErrorMessage = async (err) => {
      const data = err?.response?.data;
      if (data instanceof Blob && data.type?.includes('json')) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (json?.message) {
            const enriched = new Error(json.message);
            enriched.response = err.response;
            throw enriched;
          }
        } catch (parseErr) {
          if (parseErr?.response) throw parseErr;
        }
      }
      throw err;
    };

    if (files.length === 0) {
      try {
        const response = await apiClient.post(
          '/tts/synthesize',
          { model_id: modelId, text, params: scalars },
          { responseType: 'blob', timeout: SYNTHESIZE_TIMEOUT_MS },
        );
        return response.data;
      } catch (err) {
        await parseErrorMessage(err);
      }
    }

    const fd = new FormData();
    fd.append('model_id', String(modelId));
    fd.append('text', text);
    Object.entries(scalars).forEach(([key, value]) => {
      fd.append(key, String(value));
    });
    files.forEach(([key, file]) => {
      const name = file && file.name ? file.name : 'upload';
      fd.append(key, file, name);
    });

    try {
      const response = await apiClient.post('/tts/synthesize', fd, {
        responseType: 'blob',
        timeout: SYNTHESIZE_TIMEOUT_MS,
        transformRequest: multipartTransformRequest(),
      });
      return response.data;
    } catch (err) {
      await parseErrorMessage(err);
    }
  }
}

export default new TtsService();
